import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import {
  DatabaseError,
  DataIntegrityError,
  OpenSearchError,
} from "../auth/errors";
import { TopicId } from "../ids/definitions";
import {
  ShopperAgentConversationDetailResponse,
  ShopperAgentConversationListResponse,
  TextConciergeFilterTab,
} from "../models/shopper-ai/conversation/text-concierge";
import { DEFAULT_LIMIT, MAX_LIMIT } from "../schema/pagination";
import {
  StartShopperAgentExportInput,
  StartShopperAgentExportResponse,
} from "./shopper-agent-export";

export const ShopperAgentConversationListQueryParams = Schema.Struct({
  limit: Schema.optionalWith(
    Schema.NumberFromString.pipe(
      Schema.int(),
      Schema.positive(),
      Schema.lessThanOrEqualTo(MAX_LIMIT),
    ),
    { default: () => DEFAULT_LIMIT },
  ),
  offset: Schema.optionalWith(
    Schema.NumberFromString.pipe(Schema.int(), Schema.nonNegative()),
    { default: () => 0 },
  ),
  filterTab: Schema.optionalWith(TextConciergeFilterTab, {
    default: () => "all" as const,
  }),
  /** Playbook trigger key; only conversations with that outreach appear. */
  outreachTopic: Schema.optional(Schema.String),
  sortBy: Schema.optionalWith(
    Schema.Literal(
      "lastResponseAt",
      "createdAt",
      "lastCustomerMessageAt",
      "lastMerchantMessageAt",
    ),
    { default: () => "lastResponseAt" as const },
  ),
  sortDirection: Schema.optionalWith(Schema.Literal("asc", "desc"), {
    default: () => "desc" as const,
  }),
});
export type ShopperAgentConversationListQueryParams =
  typeof ShopperAgentConversationListQueryParams.Type;

export const ShopperAgentConversationDetailQueryParams = Schema.Struct({
  /** When set, detail may include scrollToMessageId for the latest outreach of this trigger. */
  outreachTopic: Schema.optional(Schema.String),
});
export type ShopperAgentConversationDetailQueryParams =
  typeof ShopperAgentConversationDetailQueryParams.Type;

export const TextConciergeGroup = HttpApiGroup.make("textConcierge")
  .add(
    endpoint.merchant
      .get("listTextConciergeConversations", "/text-concierge/conversations")
      .setUrlParams(ShopperAgentConversationListQueryParams)
      .addSuccess(ShopperAgentConversationListResponse)
      .addError(HttpApiError.Forbidden)
      .addError(OpenSearchError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "getTextConciergeConversationDetail",
        "/text-concierge/conversations/:topicId",
      )
      .setPath(Schema.Struct({ topicId: TopicId }))
      .setUrlParams(ShopperAgentConversationDetailQueryParams)
      .addSuccess(ShopperAgentConversationDetailResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("startShopperAgentExport", "/shopper-agent/conversations/export")
      .setPayload(StartShopperAgentExportInput)
      .addSuccess(StartShopperAgentExportResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Conflict)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
